import React, { useMemo } from 'react';

import {
  Link,
  SidePage,
  Section,
  DescriptionList,
  DescriptionItem,
  Typography,
  Tags,
  makeStyles,
} from '@gogame/components';
import { DateValueFormatter } from '@gogame/intl-ts';

import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import Actions from 'scenes/Gallery/GalleryPage/Creatives/Actions';
import {
  CreativeReviewsSummary,
  CreativeReviewsList,
  useReviewsCount,
} from 'scenes/Gallery/GalleryPage/Creatives/Reviews';
import CreativePreview from 'scenes/Gallery/GalleryPage/Creatives/components/CreativePreview';
import CreativeTargeting from 'scenes/Gallery/GalleryPage/Creatives/components/CreativeTargeting';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  AttributeList,
  AttributeItem,
} from 'scenes/Gallery/components/AttributeList';
import ExpirationDate from 'scenes/Gallery/components/ExpirationDate';
import StatusBadge from 'scenes/Gallery/components/StatusBadge';
import TrackerLink from 'scenes/Gallery/components/TrackerLink';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { valueParserObjectFromString } from 'scenes/Gallery/GalleryPage/utils/valueParsers';
import { DIMENSION_CREATIVE } from 'scenes/Trackers/Tracker/constants';
import TrackerAffiliateLink from 'scenes/Gallery/components/TrackerAffiliateLink';

const useStyles = makeStyles(() => ({
  overflowWrap: {
    overflowWrap: 'anywhere', //todo move to description list component
  },
}));

const CreativeDetails = ({
  data,
  onOpenConcept,
  onOpenBusinessPartner,
  onOpenConceptFamily,
  onOpenInNewTab,
  onRemove,
  openChildComponent,
}) => {
  const classes = useStyles();
  const {
    isAllowConceptFamilies,
    isAllowDesigner,
    isAllowSharedWith,
    isAllowRequestedBy,
    isAllowBusinessPartner,
    isAllowViewConcept,
    isAllowUseInAffiliates,
    isAllowPerformance,
    isAllowExclusiveForAffiliates,
    isAllowStatuses,
    isAllowTested,
  } = useGalleryPermissions();

  const {
    [CREATIVE.ID]: creativeId,
    [CREATIVE.HAS_STATS]: hasStats,
    [CREATIVE.JIRA_TASK]: jiraTask,
    [CREATIVE.FILE]: file,
    [CREATIVE.CREATIVE_TYPE]: creativeTypeId,
    [CREATIVE.INHERIT_TAGS]: inheritTags,
    [CREATIVE.INTERNAL_TAGS]: tags,
    [CREATIVE.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
    [CREATIVE.YOUTUBE_LINK]: youtubeLink,
    [CREATIVE.OUTSOURCE_STUDIO]: outsourceStudio,
    [CREATIVE.DESIGNER]: designer,
    [CREATIVE.AI_GENERATED]: aiGenerated,
  } = data;

  const mergedTags = useMemo(() => {
    return [...(tags || []), ...(inheritTags || [])];
  }, [inheritTags, tags]);

  const reviewsCount = useReviewsCount(creativeId);

  return (
    <SidePage.NewBody wrapper={true}>
      <SidePage.NewContent withSidebar={true}>
        <CreativePreview
          creativeType={creativeTypeId}
          fileInfo={file}
          maxWidth={688}
          maxHeight={400}
          variant={'standard'}
        />
        <Section
          title={'General'}
          action={
            <AttributeList variant={'outlined'} typographyVariant={'caption'}>
              {exclusiveForAffiliates.length &&
              isAllowExclusiveForAffiliates ? (
                <AttributeItem valueParser={'exclusiveForAffiliates'} />
              ) : null}
              {aiGenerated ? (
                <AttributeItem valueParser={'aiGenerated'} />
              ) : null}
              {hasStats && isAllowPerformance ? (
                <TrackerLink dimension={DIMENSION_CREATIVE} id={creativeId}>
                  <AttributeItem
                    valueParser={'ads'}
                    clickable={true}
                    underline={'always'}
                  />
                </TrackerLink>
              ) : null}
            </AttributeList>
          }
        >
          <DescriptionList
            stripEmpty={true}
            data={data}
            disableGutters={true}
            termWidth={'25%'}
          >
            <DescriptionItem
              label={'Name'}
              field={CREATIVE.NAME}
              type={({ value }) => (
                <Link
                  data-cy={'Concept Page'}
                  onClick={onOpenInNewTab}
                  target={'_blank'}
                >
                  <Typography>{value}</Typography>
                </Link>
              )}
            />
            {isAllowViewConcept(data) ? (
              <DescriptionItem
                label={'Concept'}
                field={CREATIVE.CONCEPT}
                linkLabelField={'name'}
                type={'link'}
                onValueClick={() =>
                  onOpenConcept(data[CREATIVE.CONCEPT][CONCEPT.ID])
                }
              />
            ) : null}
            <DescriptionItem
              label={'Start date'}
              field={CREATIVE.START_DATE}
              type={'date'}
              template={'LL'}
            />
            <DescriptionItem
              label={'Expiration date'}
              field={CREATIVE.EXPIRATION_DATE}
              type={({ value }) => <ExpirationDate value={value} />}
              template={'LL'}
            />
            {isAllowConceptFamilies ? (
              <DescriptionItem
                label={'Concept families'}
                field={`${CREATIVE.CONCEPT}.${CONCEPT.CONCEPT_FAMILIES}`}
                type={'typographyList'}
                unitsLabel={'Concept families'}
                limit={1}
                listType={'link'}
                item={({ id, name }) => (
                  <Link onClick={() => onOpenConceptFamily(id)}>
                    <Typography>{name}</Typography>
                  </Link>
                )}
              />
            ) : null}
            <DescriptionItem
              label={'Targeting'}
              value={<CreativeTargeting data={data} />}
            />
            <DescriptionItem
              label={'Internal Tags'}
              value={mergedTags}
              type={({ value }) => <Tags data={value} lineClamp={4} />}
            />
            {isAllowExclusiveForAffiliates ? (
              <DescriptionItem
                label={'Exclusive for'}
                field={CREATIVE.EXCLUSIVE_FOR_AFFILIATES}
                type={'typographyList'}
                unitsLabel={'Affiliates'}
                limit={3}
                listType={'link'}
                item={({ id, name }) => (
                  <TrackerAffiliateLink
                    id={id}
                    dimension={DIMENSION_CREATIVE}
                    drillDownId={creativeId}
                  >
                    <Typography>{name}</Typography>
                  </TrackerAffiliateLink>
                )}
              />
            ) : null}
            {hasStats && isAllowUseInAffiliates ? (
              <DescriptionItem
                label={'Used in affiliates'}
                field={CREATIVE.USED_IN_AFFILIATES}
                type={'typographyList'}
                unitsLabel={'Affiliates'}
                limit={3}
                listType={'link'}
                item={({ id, name }) => (
                  <TrackerAffiliateLink
                    id={id}
                    dimension={DIMENSION_CREATIVE}
                    drillDownId={creativeId}
                  >
                    <Typography>{name}</Typography>
                  </TrackerAffiliateLink>
                )}
              />
            ) : null}
            <DescriptionItem
              label={'Description'}
              field={CREATIVE.DESCRIPTION}
            />
            <DescriptionItem
              label={'YouTube URL'}
              type={'link'}
              value={youtubeLink}
              href={youtubeLink}
              target={'_blank'}
            />
            {isAllowTested ? (
              <DescriptionItem label={'Tested'} field={CREATIVE.TESTED} />
            ) : null}
          </DescriptionList>
        </Section>
        <Section title={`Reviews${reviewsCount ? ` (${reviewsCount})` : ''}`}>
          <CreativeReviewsList creativeId={creativeId} disableGutters={true} />
        </Section>
      </SidePage.NewContent>

      <SidePage.Sidebar
        actions={
          <Actions
            actions={data?.actions}
            data={data}
            onRemove={onRemove}
            onOpenChildComponent={openChildComponent}
            withAddComment={false}
          />
        }
      >
        <DescriptionList
          variant={'column'}
          data={data}
          stripEmpty={true}
          disableGutters={true}
        >
          {isAllowStatuses && (
            <DescriptionItem
              label={'Status'}
              field={CREATIVE.STATUS_ID}
              valueParser={valueParserObjectFromString}
              type={'status'}
              statusFactory={StatusBadge}
            />
          )}

          <DescriptionItem
            label={'Rating'}
            field={CREATIVE.RATING}
            type={({ value }) => (
              <CreativeReviewsSummary creativeId={creativeId} rating={value} />
            )}
          />
          {isAllowRequestedBy ? (
            <DescriptionItem
              label={'Requested by'}
              field={CREATIVE.REQUESTED_BY}
              type={({ value }) =>
                jiraTask ? (
                  <Link href={jiraTask} target={'_blank'}>
                    <Typography
                      noWrap
                      tooltipText={'Link to Jira Task'}
                      tooltipProps={{ wrapper: true, wrapperComponent: 'div' }}
                    >
                      {value}
                    </Typography>
                  </Link>
                ) : (
                  <Typography>{value}</Typography>
                )
              }
            />
          ) : null}
          {isAllowBusinessPartner ? (
            <DescriptionItem
              label={'Business partner'}
              field={CREATIVE.BUSINESS_PARTNERS}
              type={'link'}
              linkLabelField={'[0].name'}
              onValueClick={(_e, [{ id }]) => onOpenBusinessPartner(id)}
            />
          ) : null}
          {isAllowSharedWith ? (
            <DescriptionItem
              label={'Shared with'}
              field={CREATIVE.SHARED_WITH}
              type={'typographyList'}
              unitsLabel={'Business Partners'}
              limit={1}
              listType={'link'}
              item={({ id, name }) => (
                <Link onClick={() => onOpenBusinessPartner(id)}>
                  <Typography lineClamp={2} className={classes.overflowWrap}>
                    {name}
                  </Typography>
                </Link>
              )}
            />
          ) : null}
          <DescriptionItem
            label={'Moved from concept'}
            value={data[CREATIVE.MOVED_FROM_CONCEPT]}
            field={CREATIVE.MOVED_FROM_CONCEPT}
            type={({ value }) => (
              <div>
                <Link onClick={() => onOpenConcept(value.id)}>
                  <Typography lineClamp={2} className={classes.overflowWrap}>
                    {value.name}
                  </Typography>
                </Link>
                <Typography>
                  <DateValueFormatter
                    value={value.moveDate}
                    template={'LLL'}
                    timeZone={'local'}
                  />
                </Typography>
              </div>
            )}
          />
          {isAllowDesigner ? (
            <DescriptionItem
              label={'Designer'}
              value={
                outsourceStudio
                  ? `${outsourceStudio.name} (Outsource studio)`
                  : designer
              }
              type={({ value }) => (
                <Typography
                  sx={{
                    wordBreak: 'break-word',
                  }}
                >
                  {value}
                </Typography>
              )}
            />
          ) : null}
          <DescriptionItem
            label={'Last updated by'}
            type={'metadata'}
            value={data}
            userField={CREATIVE.UPDATED_BY}
            dateField={CREATIVE.UPDATED_DATE}
          />
          <DescriptionItem
            label={'Uploaded by'}
            type={'metadata'}
            value={data}
            userField={CREATIVE.CREATED_BY}
            dateField={CREATIVE.CREATED_DATE}
          />
        </DescriptionList>
      </SidePage.Sidebar>
    </SidePage.NewBody>
  );
};

export default CreativeDetails;
